import { FC, useMemo, useRef, useState } from 'react';
import { AlertCircle, Clock, Loader2, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOrgStore } from '@/stores/org-store';
import { useOrgQualificationContext } from '@/hooks/queries/use-org-qualification-context';
import { useQualificationBoard } from '@/hooks/queries/use-qualification-board';
import { usePromoteAttempt } from '@/hooks/mutations/use-promote-attempt';
import { useQualificationWaves } from '@/hooks/queries/use-qualification-waves';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SignOffModal } from '@/pages/workflow/component/sign-off-modal';
import { WavesPanel } from './waves-panel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BoardRow,
  QualificationStage,
} from '@/services/workflow/qualification';
import { cn } from '@/lib/utils';

const STAGE_LABELS: Record<QualificationStage, string> = {
  build: 'Build',
  pre_uat: 'Pre-UAT',
  uat: 'UAT',
  production: 'Production',
};

const STAGE_TONES: Record<QualificationStage, string> = {
  build: 'bg-muted text-foreground',
  pre_uat: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  uat: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
  production: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
};

// Radix Select cannot hold an empty-string value, so "no filter" needs a
// sentinel rather than ''.
const ALL_WAVES = 'all';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const STAGE_ORDER: QualificationStage[] = [
  'build',
  'pre_uat',
  'uat',
  'production',
];

const StageCell: FC<{ row: BoardRow }> = ({ row }) => {
  // The attempt's position, not the placement list. Promotion deliberately
  // leaves the earlier copy live so it stays findable, so placements answer
  // "where do copies exist" and would read here as being in two stages.
  const stage = row.current_stage ?? 'build';
  const retained = row.placements
    .filter((p) => p.stage !== stage)
    .sort(
      (a, b) => STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage),
    );

  return (
    <div className='space-y-1'>
      <Badge variant='secondary' className={STAGE_TONES[stage]}>
        {STAGE_LABELS[stage]}
      </Badge>
      {retained.length > 0 && (
        <span className='block text-xs text-muted-foreground'>
          Locked copy in {retained.map((p) => STAGE_LABELS[p.stage]).join(', ')}
        </span>
      )}
    </div>
  );
};

const WaitingOn: FC<{ row: BoardRow }> = ({ row }) => {
  if (row.awaiting_settlement) {
    // Both sides have signed. Promotion is an explicit admin action, so
    // this waits on a person - and saying "the system" would send nobody
    // looking for the person.
    return (
      <span className='flex items-center gap-1.5 text-sm'>
        <Clock className='size-3.5 text-muted-foreground' />
        An admin, to promote
      </span>
    );
  }
  if (!row.outstanding_sides.length) {
    return <span className='text-sm text-muted-foreground'>Nothing</span>;
  }
  return (
    <span className='text-sm'>
      {row.outstanding_sides
        .map((side) => (side === 'capitol' ? 'Capitol' : 'Client'))
        .join(' and ')}
    </span>
  );
};

export const UatBoardPage: FC = () => {
  const { currentOrg } = useOrgStore();
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [waveFilter, setWaveFilter] = useState<string>(ALL_WAVES);
  const [signOff, setSignOff] = useState<{
    orgId: string;
    workflowId: string;
    name: string;
  } | null>(null);

  // The board is per client and region, and the signed-in org's ladder
  // entry is what identifies them. An admin switches org to change which
  // client's pipeline they are looking at, which avoids a picker that
  // could name a client this deployment holds no data for.
  const {
    data: context,
    isLoading: contextLoading,
    error: contextError,
  } = useOrgQualificationContext(currentOrg?.orgid);

  const {
    data: board,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useQualificationBoard(
    context?.client,
    context?.region,
    waveFilter === ALL_WAVES ? undefined : waveFilter,
  );

  // Deliberately unfiltered, and separate from the board above. The Waves
  // tab counts how many subjects sit in EACH wave, so feeding it the
  // filtered rows made picking one wave read as "every other wave is
  // empty". With no filter selected this resolves to the same query key as
  // the board and TanStack serves both from one request; only a filtered
  // view costs a second fetch.
  const { data: unfilteredBoard } = useQualificationBoard(
    context?.client,
    context?.region,
  );

  const { data: waves } = useQualificationWaves(
    context?.client,
    context?.region,
  );

  const promote = usePromoteAttempt();
  const rows = useMemo(() => board?.rows ?? [], [board]);

  const openSignOff = (row: BoardRow) => {
    const preUat = row.placements.find((p) => p.stage === 'pre_uat');
    if (!preUat) return;
    setSignOff({
      orgId: preUat.org_id,
      workflowId: preUat.copy_workflow_id,
      name: row.workflow_name ?? row.subject_id,
    });
  };

  if (contextLoading || isLoading) {
    return (
      <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading the qualification board...
      </div>
    );
  }

  if (contextError) {
    return (
      <div className='flex items-start gap-2 p-8 text-sm'>
        <AlertCircle className='size-4 shrink-0 mt-0.5 text-muted-foreground' />
        <span>
          This organisation is not part of a qualification ladder, so it has no
          pipeline to show. Switch to an organisation that is.
        </span>
      </div>
    );
  }

  return (
    <div className='p-6 space-y-4'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h1 className='text-xl font-semibold'>UAT pipeline</h1>
          <p className='text-sm text-muted-foreground'>
            {board?.client} · {board?.region} · {rows.length}{' '}
            {rows.length === 1 ? 'workflow' : 'workflows'}
            {board &&
              board.regions_reached < board.regions_expected &&
              ` · ${board.regions_reached} of ${board.regions_expected} regions reached`}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {!!waves?.length && (
            <Select value={waveFilter} onValueChange={setWaveFilter}>
              <SelectTrigger className='w-56'>
                <SelectValue placeholder='All waves' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_WAVES}>All waves</SelectItem>
                {waves.map((wave) => (
                  <SelectItem key={wave.id} value={wave.id}>
                    {wave.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant='outline'
            onClick={() => refetch()}
            disabled={isFetching}
            className='gap-2'
          >
            <RefreshCw className={cn('size-4', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className='flex items-start gap-2 text-sm text-red-700 dark:text-red-400'>
          <AlertCircle className='size-4 shrink-0 mt-0.5' />
          <span>{error.message}</span>
        </div>
      )}

      <Tabs defaultValue='pipeline' className='space-y-4'>
        <TabsList>
          <TabsTrigger value='pipeline'>Pipeline</TabsTrigger>
          <TabsTrigger value='waves'>Waves and approvers</TabsTrigger>
        </TabsList>

        <TabsContent value='pipeline' className='space-y-4'>
          {!rows.length ? (
            <p className='text-sm text-muted-foreground'>
              {waveFilter === ALL_WAVES
                ? 'Nothing has been applied to a wave in this region yet.'
                : 'Nothing has been applied to this wave. Clear the filter to see the rest.'}
            </p>
          ) : (
            <div className='overflow-x-auto'>
              <Table tableRef={tableRef}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Wave</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Waiting on</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.subject_id}>
                      <TableCell className='font-medium'>
                        {row.workflow_name ?? row.subject_id}
                        {row.client_reference && (
                          <span className='block text-xs text-muted-foreground'>
                            {row.client_reference}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className='text-sm'>
                        {row.wave_name ?? '—'}
                      </TableCell>
                      <TableCell>
                        <StageCell row={row} />
                      </TableCell>
                      <TableCell>
                        <WaitingOn row={row} />
                      </TableCell>
                      <TableCell className='text-sm text-muted-foreground'>
                        {row.owner_user_id}
                      </TableCell>
                      <TableCell className='text-sm text-muted-foreground'>
                        {formatDate(row.updated_at)}
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex justify-end gap-2'>
                          {row.current_stage === 'pre_uat' && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => openSignOff(row)}
                            >
                              Sign-off
                            </Button>
                          )}
                          {row.awaiting_settlement && row.attempt_id && (
                            <Button
                              size='sm'
                              disabled={promote.isPending}
                              onClick={() =>
                                promote.mutate({
                                  attemptId: row.attempt_id as string,
                                  toStage: 'uat',
                                  workflowName: row.workflow_name ?? undefined,
                                })
                              }
                            >
                              Promote to UAT
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value='waves'>
          {/* Mounted on the ladder context, not on the board: wave and
              approver admin talk to different endpoints, so a failed board
              fetch should not blank this tab. */}
          {context && (
            <WavesPanel
              client={context.client}
              region={context.region}
              rows={unfilteredBoard?.rows ?? []}
            />
          )}
        </TabsContent>
      </Tabs>

      {signOff && (
        <SignOffModal
          isOpen
          onClose={() => {
            setSignOff(null);
            refetch();
          }}
          orgId={signOff.orgId}
          workflowId={signOff.workflowId}
          workflowName={signOff.name}
        />
      )}
    </div>
  );
};
